// Sensordekning per kontakt — logikken bak det viktigste skjermbildet.
//
// EIERKRAVET, ORDRETT: «Det er viktig at man også skal kunne klikke på "targets"
// og få alle sensorene som observerer denne "faren"».
//
// Og kravet som er lett å overse, men som er halve verdien: operatøren skal se
// EKSPLISITT hvilke sensorer som IKKE ser kontakten. En kontakt sett av kun én
// sensor er en annen sak enn en sett av tre. Uten den negative informasjonen må
// operatøren TELLE radene i kontaktkortet og huske hvor mange sensorer posten
// har — og det er nettopp den slags hoderegning som feiler klokka fire om natta.
//
// DERFOR returnerer `sensordekning` én rad PER SENSOR PÅ POSTEN, ikke én rad per
// observasjon. Fire sensorer gir alltid fire rader. Rader uten observasjon får
// en ÅRSAK til at de er tomme, og de årsakene er ikke likeverdige:
//   * sensoren er nede/avslått        → den KAN ikke se noe (ingen informasjon)
//   * sensoren måler, men ser ikke    → ekte negativ informasjon (verdifullt!)
//   * kontakten er utenfor rekkevidde → forventet, ingen informasjon
// En radar som måler friskt og IKKE ser noe der RF-en peker, er et av de
// sterkeste tegnene på at kontakten er liten og lav — altså kanskje en USV.
import type { KopSensor, Kystkontakt, Observasjon, Sensorart } from '../api/typer.js';
import { SENSORART_MAALER } from '../api/typer.js';
import { avstandM } from './trussel.js';
// Friskhet/sensorstatus er FLATENS, ikke kystens: «hvor gammel er målingen» har
// samme svar for hver sensor. Dekningen under er derimot kystspesifikk — se
// filens toppkommentar.
import { friskhet, sensorStatus, type SensorStatus, type Terskler, STANDARD_TERSKLER } from '@skade/sensorflate';

/** Hvorfor en sensor ikke har en observasjon av kontakten. */
export type IkkeSettGrunn =
  /** Sensoren er nede, avslått eller stille — den kan ikke uttale seg. */
  | 'kan-ikke'
  /** Sensoren måler friskt, men har ikke sett kontakten. EKTE negativ info. */
  | 'ser-ikke'
  /** Kontakten ligger utenfor sensorens oppgitte rekkevidde. */
  | 'utenfor-rekkevidde';

export const IKKE_SETT_TEKST: Record<IkkeSettGrunn, string> = {
  'kan-ikke': 'Kan ikke se — sensoren måler ikke nå',
  'ser-ikke': 'Måler, men ser ikke kontakten',
  'utenfor-rekkevidde': 'Kontakten er utenfor oppgitt rekkevidde',
};

export interface Dekningsrad {
  sensor: KopSensor;
  status: SensorStatus;
  /** Nyeste observasjon fra denne sensoren av denne kontakten, om noen. */
  observasjon?: Observasjon;
  /** Alle observasjonene fra sensoren, nyeste først — kortet viser historikken. */
  historikk: Observasjon[];
  /** Satt når `observasjon` mangler. Da er DENNE forklaringen radens innhold. */
  ikkeSett?: IkkeSettGrunn;
  /** Alder på observasjonen i ms. `null` når sensoren ikke ser kontakten. */
  alderMs: number | null;
}

export interface Dekning {
  rader: Dekningsrad[];
  /** Sensorer med en observasjon av kontakten. Dette er «hvem ser den». */
  serDen: Dekningsrad[];
  /** Sensorer uten observasjon — hver med sin årsak. Dette er «hvem gjør det ikke». */
  serDenIkke: Dekningsrad[];
  /** Unike sensorARTER som ser kontakten. Bekreftelsesgraden operatøren leser. */
  arter: Sensorart[];
  /**
   * Antall sensorer som MÅLER nå og likevel ikke ser kontakten. Er dette høyt
   * mens `arter.length` er 1, er kontakten enten svært liten eller feil.
   */
  friskeSomIkkeSer: number;
}

export interface DekningValg {
  naaMs: number;
  terskler?: Terskler;
}

/**
 * Bygg dekningen for én kontakt.
 *
 * `sensorer` er ALLE sensorene på posten — også de som ikke ser noe. Det er
 * hele hensikten; sender kalleren bare de sensorene som har observasjoner,
 * forsvinner den negative informasjonen og kortet blir like misvisende som en
 * liste over observasjoner.
 */
export function sensordekning(
  kontakt: Kystkontakt,
  sensorer: readonly KopSensor[],
  valg: DekningValg,
): Dekning {
  const t = valg.terskler ?? STANDARD_TERSKLER;
  const rader: Dekningsrad[] = sensorer.map((sensor) => {
    const mine = kontakt.observasjoner
      .filter((o) => o.sensorId === sensor.id)
      .sort((a, b) => b.tsMs - a.tsMs);
    const nyeste = mine[0];
    // Sensorens status vurderes på SENSORENS siste måling — ikke på kontaktens.
    // Ellers ville en sensor som måler travelt på alt annet sett «stille ut»
    // bare fordi den ikke ser denne ene kontakten, og det er en annen påstand.
    const status = sensorStatus(sensor, sisteMaalingFor(sensor.id, kontakt), valg.naaMs, t);

    if (nyeste) {
      return {
        sensor,
        status,
        observasjon: nyeste,
        historikk: mine,
        alderMs: Math.max(0, valg.naaMs - nyeste.tsMs),
      };
    }
    return {
      sensor,
      status,
      historikk: mine,
      alderMs: null,
      ikkeSett: ikkeSettGrunn(sensor, status, kontakt),
    };
  });

  const serDen = rader.filter((r) => r.observasjon !== undefined);
  const serDenIkke = rader.filter((r) => r.observasjon === undefined);
  return {
    rader,
    serDen,
    serDenIkke,
    arter: [...new Set(serDen.map((r) => r.sensor.art))],
    friskeSomIkkeSer: serDenIkke.filter((r) => r.ikkeSett === 'ser-ikke').length,
  };
}

/**
 * Siste gang DENNE sensoren meldte noe om DENNE kontakten. Brukes bare til
 * radens statusvurdering; postens fulle sensorstatus kommer fra sensorstolpen,
 * som ser alle observasjoner fra alle kontakter.
 */
function sisteMaalingFor(sensorId: string, kontakt: Kystkontakt): number | null {
  let siste: number | null = null;
  for (const o of kontakt.observasjoner) {
    if (o.sensorId !== sensorId) continue;
    if (siste === null || o.tsMs > siste) siste = o.tsMs;
  }
  return siste;
}

/**
 * Hvorfor ser ikke sensoren kontakten? Rekkefølgen er en rangering av hvor mye
 * informasjon svaret bærer: «kan ikke» er ingen informasjon i det hele tatt,
 * «utenfor rekkevidde» er forventet, og «ser ikke» er det ene svaret som
 * faktisk sier noe om kontakten.
 */
export function ikkeSettGrunn(
  sensor: KopSensor,
  status: SensorStatus,
  kontakt: Pick<Kystkontakt, 'lat' | 'lon'>,
): IkkeSettGrunn {
  if (status.nivaa === 'av' || status.nivaa === 'nede' || status.nivaa === 'stille') return 'kan-ikke';
  if (
    sensor.rekkeviddeM !== undefined
    && sensor.lat !== undefined && sensor.lon !== undefined
    && kontakt.lat !== undefined && kontakt.lon !== undefined
    && avstandM({ lat: sensor.lat, lon: sensor.lon }, { lat: kontakt.lat, lon: kontakt.lon }) > sensor.rekkeviddeM
  ) {
    return 'utenfor-rekkevidde';
  }
  return 'ser-ikke';
}

/**
 * Én linje som oppsummerer bekreftelsesgraden. Denne teksten er grunnen til at
 * fila finnes: den skal kunne leses på et halvt sekund og si om kontakten er et
 * faktum eller en indikasjon.
 */
export function dekningssammendrag(d: Dekning): string {
  const n = d.arter.length;
  if (n === 0) return 'Ingen sensor ser kontakten nå';
  if (n === 1) {
    const bare = d.serDen[0];
    return `Sett av ÉN sensor (${bare.sensor.navn}) — ${SENSORART_MAALER[bare.sensor.art]}. Ikke bekreftet.`;
  }
  if (n === 2) return `Bekreftet av 2 sensorarter (${d.arter.join(' + ')})`;
  return `Bekreftet av ${n} sensorarter — sterk kontakt`;
}

/**
 * Hvor sikker er kontakten SOM HELHET? Tre bånd, samme prinsipp som
 * trusselbåndene: operatøren trenger å kunne skille «dette er sant», «dette kan
 * være noe» og «dette er én sensors ord».
 */
export type Bekreftelse = 'sterk' | 'delvis' | 'enkeltkilde' | 'ingen';

export function bekreftelse(d: Dekning): Bekreftelse {
  if (d.arter.length === 0) return 'ingen';
  if (d.arter.length >= 3) return 'sterk';
  if (d.arter.length === 2) return 'delvis';
  return 'enkeltkilde';
}

export const BEKREFTELSE_NAVN: Record<Bekreftelse, string> = {
  sterk: 'Sterk kontakt',
  delvis: 'Delvis bekreftet',
  enkeltkilde: 'Én sensor',
  ingen: 'Ingen dekning nå',
};

/**
 * Har kontakten en fersk observasjon fra minst én sensor? Brukes til å dempe
 * kontakter i lista og på kartet. Skilt ut fordi «kontakten er gammel» og
 * «sensoren er stille» er to helt ulike ting som ser like ut i en tidsstempel-
 * sammenligning, og de har blitt blandet før.
 */
export function harFerskDekning(d: Dekning, naaMs: number, t: Terskler = STANDARD_TERSKLER): boolean {
  return d.serDen.some((r) => r.observasjon !== undefined && friskhet(naaMs - r.observasjon.tsMs, t) === 'fersk');
}
